import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  EventBusy as HolidayIcon,
  Print as PrintIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  DoneAll as AllPresentIcon,
  RemoveDone as AllAbsentIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format, addDays, subDays, parseISO } from 'date-fns';
import { attendanceAPI, classAPI, holidayAPI } from '../services/api';
import AttendanceCalendar from '../components/AttendanceCalendar';
import { useReactToPrint } from 'react-to-print';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const Attendance = () => {
  const monthlyReportPrintRef = useRef();
  const dailyRegisterPrintRef = useRef();

  const handlePrintMonthlyReport = useReactToPrint({
    contentRef: monthlyReportPrintRef,
  });

  const handlePrintDailyRegister = useReactToPrint({
    contentRef: dailyRegisterPrintRef,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [isHoliday, setIsHoliday] = useState(false);
  const [isSunday, setIsSunday] = useState(false);
  const [academicYearId, setAcademicYearId] = useState(null);
  const [holidayInfo, setHolidayInfo] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [dailyRegister, setDailyRegister] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchSections(selectedClass);
      const classObj = classes.find(c => c.id === parseInt(selectedClass));
      if (classObj && classObj.academicYearId) {
        setAcademicYearId(classObj.academicYearId);
      }
    }
  }, [selectedClass, classes]);

  useEffect(() => {
    checkHoliday(selectedDate);
  }, [selectedDate]);

  const fetchClasses = async () => {
    try {
      const response = await classAPI.getAll();
      const classData = response.data.data || [];
      setClasses(classData);
      if (classData.length > 0 && classData[0].academicYearId) {
        setAcademicYearId(classData[0].academicYearId);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const response = await classAPI.getSections(classId);
      setSections(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const checkHoliday = async (date) => {
    try {
      const response = await holidayAPI.checkDate(date);
      setIsHoliday(response.data.data.isHoliday);
      setIsSunday(response.data.data.isSunday);
      setHolidayInfo(response.data.data.holidayInfo);
    } catch (error) {
      console.error('Error checking holiday:', error);
    }
  };

  const fetchStudentsForAttendance = async () => {
    if (!selectedClass || !selectedSection || !selectedDate) {
      toast.warning('Please select class, section and date');
      return;
    }

    setLoading(true);
    try {
      const response = await attendanceAPI.getStudentsForAttendance({
        classId: selectedClass,
        sectionId: selectedSection,
        date: selectedDate
      });
      setStudents(response.data.data || []);
      setIsHoliday(response.data.isHoliday);
      setIsSunday(response.data.isSunday);
      setHolidayInfo(response.data.holidayInfo);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching students');
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendance = (studentId) => {
    if (isHoliday || isSunday) return;

    setStudents(prev => prev.map(s =>
      s.id === studentId
        ? { ...s, status: s.status === 'present' ? 'absent' : 'present' }
        : s
    ));
  };

  const markAll = (status) => {
    if (isHoliday || isSunday) return;
    setStudents(prev => prev.map(s => ({ ...s, status })));
  };

  const saveAttendance = async () => {
    if (isHoliday || isSunday) {
      toast.error('Cannot mark attendance on holiday/Sunday');
      return;
    }

    if (!academicYearId) {
      toast.error('Academic year not set. Please select a class first.');
      return;
    }

    setLoading(true);
    try {
      const attendanceRecords = students.map(s => ({
        studentId: s.id,
        classId: parseInt(selectedClass),
        sectionId: parseInt(selectedSection),
        status: s.status,
        academicYearId: academicYearId
      }));

      await attendanceAPI.mark({
        attendanceRecords,
        date: selectedDate
      });

      toast.success('Attendance saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving attendance');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyReport = async () => {
    if (!selectedClass || !selectedSection) {
      toast.warning('Please select class and section');
      return;
    }

    setLoading(true);
    try {
      const response = await attendanceAPI.getMonthlyReport({
        classId: selectedClass,
        sectionId: selectedSection,
        month: selectedMonth,
        year: selectedYear
      });
      setMonthlyReport(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyRegister = async () => {
    setLoading(true);
    try {
      const response = await attendanceAPI.getDailyRegister({
        date: selectedDate
      });
      setDailyRegister(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching register');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevDate = () => {
    const prev = subDays(parseISO(selectedDate), 1);
    setSelectedDate(format(prev, 'yyyy-MM-dd'));
  };

  const handleNextDate = () => {
    const next = addDays(parseISO(selectedDate), 1);
    setSelectedDate(format(next, 'yyyy-MM-dd'));
  };

  const renderMarkAttendance = () => {
    const presentCount = students.filter(s => s.status === 'present').length;
    const absentCount = students.filter(s => s.status === 'absent').length;

    return (
      <Box pb={8}> {/* Padding bottom for sticky button */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                label="Class"
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setSelectedSection('');
                  setStudents([]);
                }}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Section</InputLabel>
              <Select
                value={selectedSection}
                label="Section"
                onChange={(e) => {
                  setSelectedSection(e.target.value);
                  setStudents([]);
                }}
                disabled={!selectedClass}
              >
                {sections.map((sec) => (
                  <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>


          <Grid item xs={12} sm={4}>
            {/* Calendar Toggle / Date Display */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'white',
                p: 1,
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                cursor: 'pointer'
              }}
              onClick={() => setShowCalendar(!showCalendar)}
            >
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handlePrevDate(); }}>
                <PrevIcon fontSize="small" />
              </IconButton>

              <Typography variant="body2" fontWeight="bold">
                {format(parseISO(selectedDate), 'EEE, dd MMM yyyy')}
              </Typography>

              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleNextDate(); }}>
                <NextIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchStudentsForAttendance}
              startIcon={<RefreshIcon />}
              disabled={loading}
              sx={{ height: '40px' }}
            >
              Load
            </Button>
          </Grid>
        </Grid>

        {showCalendar && (
          <AttendanceCalendar
            currentDate={parseISO(selectedDate)}
            onDateClick={(date) => {
              setSelectedDate(format(date, 'yyyy-MM-dd'));
              setShowCalendar(false);
            }}
            onMonthChange={(date) => setSelectedDate(format(date, 'yyyy-MM-dd'))}
          // In a real app, pass attendanceData here used for dots
          />
        )}

        {(isHoliday || isSunday) && (
          <Alert severity="warning" icon={<HolidayIcon />} sx={{ mb: 2 }}>
            {isSunday ? 'Sunday - Attendance marking disabled' :
              `Holiday: ${holidayInfo?.name} - Attendance marking disabled`}
          </Alert>
        )}

        {students.length > 0 && (
          <>
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {format(parseISO(selectedDate), 'dd MMM yyyy')}
                </Typography>
                <Box>
                  <Typography component="span" color="success.main" fontWeight="bold" sx={{ mr: 2 }}>
                    P: {presentCount}
                  </Typography>
                  <Typography component="span" color="error.main" fontWeight="bold">
                    A: {absentCount}
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  size="small"
                  startIcon={<AllPresentIcon />}
                  onClick={() => markAll('present')}
                  disabled={isHoliday || isSunday}
                  sx={{ color: 'success.main', borderColor: 'success.main' }}
                  variant="outlined"
                >
                  All Present
                </Button>
                <Button
                  size="small"
                  startIcon={<AllAbsentIcon />}
                  onClick={() => markAll('absent')}
                  disabled={isHoliday || isSunday}
                  sx={{ color: 'error.main', borderColor: 'error.main' }}
                  variant="outlined"
                >
                  All Absent
                </Button>
              </Stack>
            </Paper>

            <Alert severity="info" sx={{ mb: 2, py: 0 }}>
              Tap on student cards to toggle status
            </Alert>

            <Grid container spacing={2}>
              {students.map((student) => {
                const isAbsent = student.status === 'absent';
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={student.id}>
                    <Card
                      elevation={isAbsent ? 3 : 1}
                      sx={{
                        borderRadius: 3,
                        border: isAbsent ? '2px solid #ef5350' : '2px solid #e0e0e0', // Red border if absent
                        bgcolor: isAbsent ? '#ffebee' : '#f0fdf4',
                        transition: 'all 0.2s',
                        height: '100%'
                      }}
                    >
                      <CardActionArea
                        onClick={() => toggleAttendance(student.id)}
                        disabled={isHoliday || isSunday}
                        sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            sx={{
                              width: 60,
                              height: 60,
                              mb: 1.5,
                              bgcolor: isAbsent ? '#ef5350' : '#4caf50',
                              fontSize: '1.5rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {student.rollNumber}
                          </Avatar>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              position: 'absolute',
                              bottom: 10,
                              right: -5,
                              bgcolor: isAbsent ? '#d32f2f' : '#2e7d32',
                              border: '2px solid white'
                            }}
                          >
                            {isAbsent ? <CloseIcon sx={{ fontSize: 16 }} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                          </Avatar>
                        </Box>

                        <Typography variant="body1" align="center" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                          {student.firstName}
                        </Typography>
                        <Typography variant="body2" align="center" noWrap sx={{ maxWidth: '100%' }}>
                          {student.lastName}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            fontWeight: 'bold',
                            color: isAbsent ? 'error.main' : 'success.main',
                            textTransform: 'uppercase',
                            letterSpacing: 1
                          }}
                        >
                          {isAbsent ? 'ABSENT' : 'PRESENT'}
                        </Typography>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Sticky Save Button */}
            <Paper
              elevation={3}
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                zIndex: 1000,
                display: 'flex',
                justifyContent: 'center',
                bgcolor: 'white',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                onClick={saveAttendance}
                disabled={loading || isHoliday || isSunday}
                sx={{
                  borderRadius: 28,
                  px: 6,
                  bgcolor: '#5c6bc0',
                  '&:hover': { bgcolor: '#3949ab' }
                }}
              >
                Save Attendance
              </Button>
            </Paper>
          </>
        )}
      </Box>
    );
  };

  const exportMonthlyReportExcel = async () => {
    if (!monthlyReport) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Monthly Attendance');

    // Add metadata/headers
    worksheet.mergeCells('A1:E1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = `Monthly Attendance Report - ${monthlyReport.className} (${monthlyReport.sectionName})`;
    titleCell.font = { bold: true, size: 16 };
    titleCell.alignment = { horizontal: 'center' };

    worksheet.mergeCells('A2:E2');
    const subtitleCell = worksheet.getCell('A2');
    const monthName = new Date(2000, monthlyReport.month - 1).toLocaleString('default', { month: 'long' });
    subtitleCell.value = `Month: ${monthName} ${monthlyReport.year} | Working Days: ${monthlyReport.totalWorkingDays} | Avg Attendance: ${monthlyReport.average}`;
    subtitleCell.font = { italic: true, size: 12 };
    subtitleCell.alignment = { horizontal: 'center' };

    worksheet.addRow([]);

    // Table headers
    const headerRow = worksheet.addRow(['Roll No', 'Name', 'Present Days', 'Absent Days', 'Percentage (%)']);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1A9E8F' }
      };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Student data
    monthlyReport.students.forEach(student => {
      const row = worksheet.addRow([
        student.rollNumber,
        student.name,
        student.presentDays,
        student.absentDays,
        `${student.percentage}%`
      ]);
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    worksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, cell => {
        const columnLength = cell.value ? cell.value.toString().length : 0;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = Math.max(maxLength + 3, 10);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Monthly_Attendance_${monthlyReport.className}_${monthlyReport.sectionName}_${monthName}_${monthlyReport.year}.xlsx`);
  };

  const exportDailyRegisterExcel = async () => {
    if (!dailyRegister) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Daily Register');

    worksheet.mergeCells('A1:J1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = `Daily Attendance Register - ${format(parseISO(selectedDate), 'dd MMM yyyy')}`;
    titleCell.font = { bold: true, size: 16 };
    titleCell.alignment = { horizontal: 'center' };

    worksheet.addRow([]);

    worksheet.addRow([
      'Class & Division',
      'Total Strength', '', '',
      'Present', '', '',
      'Absent', '', ''
    ]);
    worksheet.mergeCells('B3:D3');
    worksheet.mergeCells('E3:G3');
    worksheet.mergeCells('H3:J3');

    worksheet.addRow([
      '',
      'Boys', 'Girls', 'Total',
      'Boys', 'Girls', 'Total',
      'Boys', 'Girls', 'Total'
    ]);

    worksheet.mergeCells('A3:A4');

    const headerRows = [worksheet.getRow(3), worksheet.getRow(4)];
    headerRows.forEach(row => {
      row.font = { bold: true };
      row.eachCell(cell => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1A9E8F' }
        };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    dailyRegister.register.forEach(row => {
      const dataRow = worksheet.addRow([
        `${row.className} - ${row.sectionName}`,
        row.total.boys,
        row.total.girls,
        row.total.total,
        row.present.boys,
        row.present.girls,
        row.present.total,
        row.absent.boys,
        row.absent.girls,
        row.absent.total
      ]);
      dataRow.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { horizontal: 'center' };
      });
      dataRow.getCell(1).alignment = { horizontal: 'left' };
    });

    if (dailyRegister.grandTotal) {
      const gt = dailyRegister.grandTotal;
      const gtRow = worksheet.addRow([
        'GRAND TOTAL',
        gt.total.boys,
        gt.total.girls,
        gt.total.total,
        gt.present.boys,
        gt.present.girls,
        gt.present.total,
        gt.absent.boys,
        gt.absent.girls,
        gt.absent.total
      ]);
      gtRow.eachCell(cell => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0E0E0' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { horizontal: 'center' };
      });
      gtRow.getCell(1).alignment = { horizontal: 'left' };
    }

    worksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, cell => {
        const columnLength = cell.value ? cell.value.toString().length : 0;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = Math.max(maxLength + 3, 10);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Daily_Attendance_Register_${selectedDate}.xlsx`);
  };

  const renderMonthlyReport = () => (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Class</InputLabel>
            <Select
              value={selectedClass}
              label="Class"
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedSection('');
              }}
            >
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Section</InputLabel>
            <Select
              value={selectedSection}
              label="Section"
              onChange={(e) => setSelectedSection(e.target.value)}
              disabled={!selectedClass}
            >
              {sections.map((sec) => (
                <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {[...Array(12)].map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={fetchMonthlyReport}
            disabled={loading}
          >
            Generate
          </Button>
        </Grid>
      </Grid>

      {monthlyReport && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={exportMonthlyReportExcel}
            >
              Export Excel
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={handlePrintMonthlyReport}
            >
              Print
            </Button>
          </Box>

          <Box ref={monthlyReportPrintRef} sx={{ p: 2 }}>
            <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #1a9e8f', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color="primary">
                Monthly Attendance Report
              </Typography>
              <Typography variant="subtitle1" fontWeight="medium" color="textSecondary" sx={{ mt: 0.5 }}>
                Class: {monthlyReport.className} - {monthlyReport.sectionName} | Month: {new Date(2000, monthlyReport.month - 1).toLocaleString('default', { month: 'long' })} {monthlyReport.year}
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: '#e3f2fd' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Working Days</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">{monthlyReport.totalWorkingDays}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: '#f3e5f5' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Total Students</Typography>
                    <Typography variant="h5" fontWeight="bold" color="secondary">{monthlyReport.totalStudents}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: '#e8f5e9' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Avg Attendance</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">{monthlyReport.average}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ bgcolor: '#fff3e0' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Overall %</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#ed6c02' }}>{monthlyReport.percentage}%</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Roll No</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell align="center"><strong>P</strong></TableCell>
                    <TableCell align="center"><strong>A</strong></TableCell>
                    <TableCell align="center"><strong>%</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyReport.students?.map((student) => (
                    <TableRow key={student.studentId} hover>
                      <TableCell>{student.rollNumber}</TableCell>
                      <TableCell>{student.name}</TableCell>
                      <TableCell align="center" sx={{ color: 'success.main' }}><b>{student.presentDays}</b></TableCell>
                      <TableCell align="center" sx={{ color: 'error.main' }}><b>{student.absentDays}</b></TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${student.percentage}%`}
                          size="small"
                          color={parseFloat(student.percentage) < 75 ? 'error' : 'success'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );

  const renderDailyRegister = () => (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={fetchDailyRegister}
            disabled={loading}
          >
            Show Register
          </Button>
        </Grid>
      </Grid>

      {dailyRegister && (
        <Box sx={{ mt: 3 }}>
          {dailyRegister.register?.length === 0 ? (
            <Alert severity="warning">No attendance data found for {selectedDate}</Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={exportDailyRegisterExcel}
                >
                  Export Excel
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintDailyRegister}
                >
                  Print
                </Button>
              </Box>

              <Box ref={dailyRegisterPrintRef} sx={{ p: 2 }}>
                <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #1a9e8f', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    Daily Attendance Register
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium" color="textSecondary" sx={{ mt: 0.5 }}>
                    Date: {format(parseISO(selectedDate), 'EEEE, dd MMM yyyy')}
                  </Typography>
                </Box>

                <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1a9e8f' }}>
                        <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                          Class & Division
                        </TableCell>
                        <TableCell colSpan={3} align="center" sx={{ color: 'white', fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                          Total Strength
                        </TableCell>
                        <TableCell colSpan={3} align="center" sx={{ color: 'white', fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                          Present
                        </TableCell>
                        <TableCell colSpan={3} align="center" sx={{ color: 'white', fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                          Absent
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Boys</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Girls</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Total</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Boys</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Girls</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Total</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Boys</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Girls</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailyRegister.register.map((row, index) => (
                        <TableRow key={index} hover>
                          <TableCell align="left" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                            {row.className} - {row.sectionName}
                          </TableCell>
                          <TableCell align="center" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{row.total.boys}</TableCell>
                          <TableCell align="center" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{row.total.girls}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>{row.total.total}</TableCell>
                          <TableCell align="center" sx={{ color: 'success.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{row.present.boys}</TableCell>
                          <TableCell align="center" sx={{ color: 'success.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{row.present.girls}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'success.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{row.present.total}</TableCell>
                          <TableCell align="center" sx={{ color: 'error.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{row.absent.boys}</TableCell>
                          <TableCell align="center" sx={{ color: 'error.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{row.absent.girls}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'error.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{row.absent.total}</TableCell>
                        </TableRow>
                      ))}

                      {dailyRegister.grandTotal && (
                        <TableRow sx={{ backgroundColor: '#eeeeee', fontWeight: 'bold' }}>
                          <TableCell align="left" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>
                            GRAND TOTAL
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.total.boys}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.total.girls}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.total.total}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'success.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.present.boys}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'success.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.present.girls}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'success.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.present.total}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'error.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.absent.boys}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'error.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.absent.girls}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'error.main', border: '1px solid rgba(224, 224, 224, 1)' }}>{dailyRegister.grandTotal.absent.total}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold">
          Attendance
        </Typography>
        <Chip label="Mobile Optimized" color="primary" size="small" variant="outlined" sx={{ display: { xs: 'inline-flex', md: 'none' } }} />
      </Box>

      <Paper sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Mark" />
          <Tab label="Report" />
          <Tab label="Register" />
        </Tabs>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {activeTab === 0 && renderMarkAttendance()}
      {activeTab === 1 && renderMonthlyReport()}
      {activeTab === 2 && renderDailyRegister()}
    </Box>
  );
};

export default Attendance;
